import React from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  UISpec,
  LayoutNode,
  PanelNode,
  MarkdownNode,
  RadioListNode,
  TableNode,
  TextNode,
} from '../types';

/**
 * SpecRenderer — recursive renderer for UISpec node trees.
 * Used today for optional `step.extras` (custom controls inside a step card)
 * and any free-form panels the LLM may invent in M2+.
 */
export function SpecRenderer({ node }: { node: UISpec }) {
  switch (node.type) {
    case 'layout':
      return <Layout node={node} />;
    case 'panel':
      return <Panel node={node} />;
    case 'markdown':
      return <Markdown node={node} />;
    case 'radiolist':
      return <RadioList node={node} />;
    case 'table':
      return <Table node={node} />;
    case 'text':
      return <Text node={node} />;
    default: {
      const unknown: unknown = node;
      return (
        <pre className="artifact-pre">{JSON.stringify(unknown, null, 2)}</pre>
      );
    }
  }
}

function Layout({ node }: { node: LayoutNode }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: node.direction,
        gap: node.gap ?? 8,
      }}
    >
      {node.children.map((c, i) => (
        <SpecRenderer key={i} node={c} />
      ))}
    </div>
  );
}

function Panel({ node }: { node: PanelNode }) {
  return (
    <section className="artifact-block" style={{ background: '#fff' }}>
      {node.title && (
        <div className="artifact-file-hd">
          <span className="artifact-file-name">{node.title}</span>
        </div>
      )}
      <div style={{ padding: 12 }}>
        {node.subtitle && (
          <div
            style={{ fontSize: 12, color: '#6b778c', marginBottom: 8 }}
          >
            {node.subtitle}
          </div>
        )}
        {node.children.map((c, i) => (
          <SpecRenderer key={i} node={c} />
        ))}
      </div>
    </section>
  );
}

function Markdown({ node }: { node: MarkdownNode }) {
  const html = React.useMemo(() => {
    try {
      return DOMPurify.sanitize(marked.parse(node.content, { async: false }) as string);
    } catch {
      return escape(node.content);
    }
  }, [node.content]);
  return (
    <div className="artifact-md" dangerouslySetInnerHTML={{ __html: html }} />
  );
}

function RadioList({ node }: { node: RadioListNode }) {
  return (
    <>
      {node.options.map((opt) => (
        <div
          key={opt.value}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 10px',
            borderRadius: 4,
            border: opt.selected ? '2px solid #0C66E4' : '1px solid #DFE1E6',
            background: opt.selected ? '#E8F0FE' : '#fff',
            cursor: 'pointer',
            marginBottom: 6,
          }}
        >
          <input
            type="radio"
            name={node.name}
            defaultChecked={opt.selected}
            style={{ margin: 0, flexShrink: 0 }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#172B4D' }}>
              {opt.label}
            </div>
            {opt.sub && (
              <div style={{ fontSize: 11, color: '#6B778C' }}>{opt.sub}</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <button
              className="step-card-btn subtle"
              style={{ fontSize: 11, padding: '2px 8px' }}
            >
              Edit
            </button>
            <button
              className="step-card-btn subtle"
              style={{ fontSize: 11, padding: '2px 8px' }}
            >
              Delete
            </button>
          </div>
        </div>
      ))}
      {node.addLabel && (
        <button
          className="step-card-btn subtle"
          style={{ fontSize: 12, color: '#6B778C' }}
        >
          {node.addLabel}
        </button>
      )}
    </>
  );
}

function Table({ node }: { node: TableNode }) {
  return (
    <div className="artifact-md">
      <table>
        <thead>
          <tr>
            {node.columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {node.rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{String(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Text({ node }: { node: TextNode }) {
  const size = node.size === 'lg' ? 16 : node.size === 'sm' ? 12 : 13;
  return (
    <p
      style={{
        fontSize: size,
        color: node.muted ? '#6b778c' : '#172b4d',
        margin: '0 0 8px',
      }}
    >
      {node.content}
    </p>
  );
}

function escape(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
